import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  PlusIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline'
import { bundleService } from '../../services/bundleService'
import toast from 'react-hot-toast'

export function BundleInstancesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedBundle, setSelectedBundle] = useState<any>(null)
  const [showAssembleModal, setShowAssembleModal] = useState(false)
  const [selectedSerials, setSelectedSerials] = useState<Record<number, number[]>>({})

  const { data: bundleDetailData, isLoading: loadingBundleDetail } = useQuery({
    queryKey: ['bundle-detail', selectedBundle?.bundle_id],
    queryFn: () => bundleService.getById(selectedBundle.bundle_id),
    enabled: !!selectedBundle?.bundle_id && showAssembleModal,
  })

  const bundleDetail = bundleDetailData?.data?.data

  const { data: bundlesData, isLoading: loadingBundles } = useQuery({
    queryKey: ['bundles'],
    queryFn: () => bundleService.getAll({ limit: 100 }),
  })

  const { data: instancesData, isLoading: loadingInstances } = useQuery({
    queryKey: ['bundle-instances'],
    queryFn: () => bundleService.getInstances({ limit: 100 }),
  })

  const createInstanceMutation = useMutation({
    mutationFn: (data: { bundle_id: number; serials: Array<{ component_id: number; serial_id: number }> }) =>
      bundleService.createInstance(data),
    onSuccess: () => {
      toast.success('Bundle instance assembled successfully')
      queryClient.invalidateQueries({ queryKey: ['bundle-instances'] })
      setShowAssembleModal(false)
      setSelectedBundle(null)
      setSelectedSerials({})
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to assemble bundle')
    },
  })

  const bundles = bundlesData?.data?.data?.items || []
  const instances = instancesData?.data?.data?.items || []

  const handleAssemble = () => {
    if (!selectedBundle) return
    const serials: Array<{ component_id: number; serial_id: number }> = []
    for (const componentId in selectedSerials) {
      for (const serialId of selectedSerials[componentId]) {
        serials.push({ component_id: parseInt(componentId), serial_id: serialId })
      }
    }

    // Validate counts
    for (const comp of selectedBundle.bundle_components) {
      const count = selectedSerials[comp.component_id]?.length || 0
      if (count !== comp.quantity) {
        toast.error(`${comp.products.product_name} requires ${comp.quantity} serials but ${count} selected`)
        return
      }
    }

    createInstanceMutation.mutate({
      bundle_id: selectedBundle.bundle_id,
      serials,
    })
  }

  const toggleSerial = (componentId: number, serialId: number) => {
    setSelectedSerials(prev => {
      const current = prev[componentId] || []
      if (current.includes(serialId)) {
        return { ...prev, [componentId]: current.filter(id => id !== serialId) }
      }
      return { ...prev, [componentId]: [...current, serialId] }
    })
  }

  const openAssembleModal = (bundle: any) => {
    setSelectedBundle(bundle)
    setSelectedSerials({})
    setShowAssembleModal(true)
  }

  const modalBundle = bundleDetail || selectedBundle

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/bundles')} className="text-gray-600 hover:text-gray-900">
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bundle Instances</h1>
            <p className="text-gray-500 mt-1">Assembled bundles with specific serial numbers</p>
          </div>
        </div>
      </div>

      {/* Existing Instances */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Assembled Instances</h2>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Instance ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bundle</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Components</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loadingInstances ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                </td>
              </tr>
            ) : instances.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No bundle instances assembled yet
                </td>
              </tr>
            ) : (
              instances.map((instance: any) => (
                <tr key={instance.instance_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">#{instance.instance_id}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{instance.product_bundles?.bundle_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {instance.bundle_instance_components?.map((bic: any) => (
                      <div key={bic.id} className="text-xs">
                        {bic.bundle_components?.products?.product_name}: {bic.serial_numbers?.serial_number}
                      </div>
                    ))}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      instance.status === 'available' ? 'bg-green-100 text-green-800' :
                      instance.status === 'sold' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {instance.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(instance.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Available Bundles to Assemble */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Available Bundles</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {loadingBundles ? (
            <div className="px-6 py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
            </div>
          ) : bundles.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No bundles found
            </div>
          ) : (
            bundles.map((bundle: any) => (
              <div key={bundle.bundle_id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div>
                  <p className="font-medium text-gray-900">{bundle.bundle_name}</p>
                  <p className="text-sm text-gray-500">SKU: {bundle.bundle_sku}</p>
                  <p className="text-sm text-gray-500">
                    {bundle.bundle_components?.length} components
                  </p>
                </div>
                <button
                  onClick={() => openAssembleModal(bundle)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <PlusIcon className="w-4 h-4" />
                  Assemble
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Assemble Modal */}
      {showAssembleModal && selectedBundle && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowAssembleModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Assemble: {selectedBundle.bundle_name}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Select specific serial numbers for each component in this bundle.
              </p>

              {loadingBundleDetail ? (
                <div className="py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                  <p className="text-sm text-gray-500 mt-2">Loading available serials...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {modalBundle.bundle_components?.map((comp: any) => (
                    <div key={comp.component_id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-900">
                          {comp.products?.product_name}
                          <span className="text-sm text-gray-500 ml-2">
                            (Need {comp.quantity})
                          </span>
                        </h3>
                        <span className="text-xs text-gray-500">
                          Selected: {(selectedSerials[comp.component_id] || []).length} / {comp.quantity}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                        {comp.products?.serial_numbers?.map((serial: any) => (
                          <label
                            key={serial.serial_id}
                            className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                              (selectedSerials[comp.component_id] || []).includes(serial.serial_id)
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={(selectedSerials[comp.component_id] || []).includes(serial.serial_id)}
                              onChange={() => toggleSerial(comp.component_id, serial.serial_id)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm font-medium">{serial.serial_number}</span>
                          </label>
                        ))}
                        {(!comp.products?.serial_numbers || comp.products.serial_numbers.length === 0) && (
                          <p className="text-sm text-gray-500 col-span-2">No available serials for this product</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowAssembleModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssemble}
                  disabled={createInstanceMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {createInstanceMutation.isPending ? 'Assembling...' : 'Assemble Bundle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
